import React, { useState } from 'react';
import { Stack } from 'react-bootstrap';
import {
  Button,
  Card,
  FormattedMessageDiv,
  InputSliderCombo,
  LoadingAnimation,
  Navigation,
  useWizardContext,
} from '@odmonorepo/core-components';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Offer } from 'merchant-api/types';
import { useDetailedOffers } from 'merchant-api/offer';
import { checkout, getCheckoutState, updateFirstDrawAmount } from 'merchant-api/checkout';

const calculateEstimate = (selectedOffer: Offer, amount: number): number => {
  const paymentsInYear =
    selectedOffer.pricedDetails.paymentFrequency.toUpperCase() === 'WEEKLY'
      ? 52: 12;
  const periodInterest =
    selectedOffer.pricedDetails.trueAPR / 100 / paymentsInYear;
  const one_plus_i_to_nth = Math.pow(1 + periodInterest, selectedOffer.pricedDetails.numberOfPayments);
  const derivative =
    (periodInterest * one_plus_i_to_nth) / (one_plus_i_to_nth - 1);
  const estimatedPaymentAmount = amount * derivative;

  return Math.round(estimatedPaymentAmount * 100) / 100;
};

export const RequiredFirstDraw = () => {
  const [drawAmount, setDrawAmount] = useState<number>(0);
  const [estimate, setEstimate] = useState<number>(0);
  const { checkoutData, isLoadingCheckoutState } = getCheckoutState();

  const { selectedOffer, isLoadingDetailedOffers } = useDetailedOffers();
  const { nextStep, goToStep } = useWizardContext();
  const { trigger } = updateFirstDrawAmount();

  if (checkoutData?.identityVerificationStatus === 'FAIL') {
    goToStep('IDENTITY_FAILED');
  }

  if (isLoadingDetailedOffers || isLoadingCheckoutState || !selectedOffer || !selectedOffer.pricedDetails) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  return (
    <Stack
      gap={6}
      className="mx-auto"
      style={{ width: '100%', maxWidth: '600px' }}
    >
      <FormattedMessageDiv
        id="checkout.requiredFirstDraw.title"
        className="fs-3 fw-semibold text-secondary ms-1 text-center"
      />

      <Card bodyClassName="p-md-6 px-md-8 p-4">
        <Stack gap={4}>
          <Stack gap={1}>
          <FormattedMessageDiv
            id="checkout.requiredFirstDraw.selectAmount"
            className="fw-semibold fs-4"
          />

          <FormattedMessageDiv
            id="checkout.requiredFirstDraw.minimumRequired"
            className='pb-5'
            values={{
              amount: (
                <FormattedNumber
                  value={selectedOffer.requiredInitialDraw}
                  style="currency"
                  currency="USD"
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                />
              ),
            }}
          />

          <InputSliderCombo
            min={selectedOffer.requiredInitialDraw}
            max={selectedOffer.pricedDetails.lineAmount}
            initialValue={
              Number(checkoutData?.selectedDrawAmount) ||
              selectedOffer.pricedDetails.lineAmount / 2 +
              selectedOffer.requiredInitialDraw / 2
            }
            onChange={(value: number): void => {
              setDrawAmount(value);
              setEstimate(calculateEstimate(selectedOffer, value));
            }}
            className="fs-8"
          />
          </Stack>
         <hr className="border-medium-gray" />

          <Stack gap={1}>
          <FormattedMessageDiv
            id="checkout.requiredFirstDraw.repaymentTitle"
            className="fw-semibold fs-4"
          />

          <FormattedMessageDiv
            id={
              selectedOffer.pricedDetails.paymentFrequency.toUpperCase() === 'WEEKLY'
                ? 'checkout.requiredFirstDraw.repaymentEstimateWeekly'
                : 'checkout.requiredFirstDraw.repaymentEstimateMonthly'
            }
            values={{
              b: (text: string) => <b>{text}</b>,
              amount: (
                <FormattedNumber
                  value={estimate}
                  style="currency"
                  currency="USD"
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                />
              ),
              payments: selectedOffer.pricedDetails.numberOfPayments
                ? selectedOffer.pricedDetails.numberOfPayments
                : selectedOffer.pricedDetails.paymentFrequency.toUpperCase() === 'WEEKLY'
                ? 52
                : 12,
            }}
          />
          </Stack>
         <hr className="border-medium-gray" />

          <FormattedMessageDiv id="checkout.requiredFirstDraw.afterCheckout" />
        </Stack>
      </Card>

      <Navigation validate={false} continueOnly continueText={<FormattedMessageDiv id="checkout.requiredFirstDraw.continueToAgreement" />} handleNext={() => {
        trigger({
          amount: drawAmount,
          estimatedPaymentAmount: estimate.toFixed(0)
        }).then(() => {
          nextStep();
        }).catch(() => {
          throw new Error('Error updating first draw amount');
        });
      }}/>
    </Stack>
  );
};

export default RequiredFirstDraw;